/**
 * Bulk Import Section - variant-aware (Text, List, Gateway, Path, Lander, Offer).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState, useCallback } from 'react';
import { ChevronDown, Copy, FileDown } from 'lucide-react';

import { Button, Card, CardContent, CardTitle, CardDescription } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import {
  useBulkCreateTagRulesForTab,
  type CreateListTagRuleDto,
  type TagRuleCondition,
} from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { getRangeFromPreset } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { StatsDateRangePreset } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import {
  BULK_MAX_RULES,
  LIST_METRIC_FIELDS,
  OPERATORS,
} from '@/pages/admin/ListTagRulesPage/constants';
import { parseBulkImportCondition } from '@/pages/admin/ListTagRulesPage/bulk-import-condition.util';
import { buildBulkImportPromptForVariant } from '@/pages/admin/ListTagRulesPage/bulkImportSchemaByVariant';
import type { TagRuleTabVariant } from '@/hooks';
import { applyHourBulkRules } from '@/features/hour-tags';

const ALLOWED_FIELDS = new Set(LIST_METRIC_FIELDS.map((f) => f.key));
const ALLOWED_OPS = new Set(OPERATORS.map((o) => o.op));

function validateBulkJson(
  raw: unknown,
): { ok: true; rules: CreateListTagRuleDto[] } | { ok: false; error: string } {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, error: 'Invalid JSON: expected an object' };
  }
  const obj = raw as Record<string, unknown>;
  const rules = obj.rules;
  if (!Array.isArray(rules)) {
    return { ok: false, error: 'Missing or invalid "rules" array' };
  }
  if (rules.length === 0) {
    return { ok: false, error: 'rules array must not be empty' };
  }
  if (rules.length > BULK_MAX_RULES) {
    return { ok: false, error: `Maximum ${BULK_MAX_RULES} rules per import` };
  }
  const dtos: CreateListTagRuleDto[] = [];
  for (let i = 0; i < rules.length; i++) {
    const r = rules[i];
    if (!r || typeof r !== 'object') {
      return { ok: false, error: `Rule ${i + 1}: invalid rule object` };
    }
    const rule = r as Record<string, unknown>;
    const name = rule.name;
    if (typeof name !== 'string' || !name.trim()) {
      return { ok: false, error: `Rule ${i + 1}: name is required` };
    }
    if (name.length > 80) {
      return { ok: false, error: `Rule ${i + 1}: name must be at most 80 characters` };
    }
    const conditions = rule.conditions;
    if (!Array.isArray(conditions) || conditions.length === 0) {
      return { ok: false, error: `Rule ${i + 1}: conditions must be a non-empty array` };
    }
    const parsedConditions: TagRuleCondition[] = [];
    for (let j = 0; j < conditions.length; j++) {
      const c = conditions[j];
      if (!c || typeof c !== 'object') {
        return { ok: false, error: `Rule ${i + 1}, condition ${j + 1}: invalid condition` };
      }
      const cond = c as Record<string, unknown>;
      const parsed = parseBulkImportCondition(cond, i, j, ALLOWED_FIELDS, ALLOWED_OPS);
      if (!parsed.ok) return { ok: false, error: parsed.error };
      parsedConditions.push(parsed.condition);
    }
    const preset = (rule.dateRangePreset as string) ?? 'last-30-days';
    const ruleToDateIsToday = rule.toDateIsToday === true;
    if (preset === 'custom') {
      const from = rule.fromDate;
      const to = rule.toDate;
      if (typeof from !== 'string' || !from.trim()) {
        return {
          ok: false,
          error: `Rule ${i + 1}: fromDate required when dateRangePreset is custom`,
        };
      }
      if (!ruleToDateIsToday && (typeof to !== 'string' || !to.trim())) {
        return {
          ok: false,
          error: `Rule ${i + 1}: toDate required when dateRangePreset is custom and toDateIsToday is false`,
        };
      }
    }
    const color = rule.color;
    const dto: CreateListTagRuleDto = {
      name: name.trim(),
      conditions: parsedConditions,
      dateRangePreset: preset as StatsDateRangePreset,
      enabled: rule.enabled !== false,
      groupKey:
        typeof rule.groupKey === 'string' && rule.groupKey.trim()
          ? rule.groupKey.trim()
          : undefined,
    };
    if (ruleToDateIsToday) {
      dto.toDateIsToday = true;
    }
    if (typeof color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(color.trim())) {
      dto.color = color.trim();
    }
    if (preset === 'custom') {
      dto.fromDate = (rule.fromDate as string).trim();
      if (!ruleToDateIsToday) {
        dto.toDate = (rule.toDate as string).trim();
      }
    } else {
      const range = getRangeFromPreset(preset as StatsDateRangePreset);
      if (range) {
        dto.fromDate = range.from;
        dto.toDate = range.to;
      }
    }
    dtos.push(dto);
  }
  return { ok: true, rules: dtos };
}

interface BulkImportSectionVariantProps {
  variant: TagRuleTabVariant;
}

export const BulkImportSectionVariant: FC<BulkImportSectionVariantProps> = ({
  variant,
}) => {
  const [open, setOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [userContext, setUserContext] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const { success: toastSuccess, error: toastError } = useToast();
  const bulkCreate = useBulkCreateTagRulesForTab(variant);
  const fullPrompt = buildBulkImportPromptForVariant(variant, userContext);

  const handleCopyPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullPrompt);
      toastSuccess('Copied', 'AI prompt copied to clipboard. Paste into Gemini or ChatGPT.');
    } catch {
      toastError('Copy failed', 'Could not copy to clipboard.');
    }
  }, [fullPrompt, toastSuccess, toastError]);

  const handleImport = useCallback(async () => {
    setValidationError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      setValidationError('Invalid JSON');
      return;
    }
    const shared = validateBulkJson(parsed);
    const result =
      shared.ok && variant === 'hour' ? applyHourBulkRules(parsed, shared.rules) : shared;
    if (!result.ok) {
      setValidationError(result.error);
      return;
    }
    try {
      const res = await bulkCreate.mutateAsync(result.rules);
      const count = res?.created ?? (res as { data?: unknown[] })?.data?.length ?? result.rules.length;
      toastSuccess('Rules created', `${count} rule${count !== 1 ? 's' : ''} created.`);
      setJsonInput('');
    } catch (e) {
      toastError('Import failed', getApiErrorMessage(e, 'Could not create rules'));
    }
  }, [jsonInput, bulkCreate, toastSuccess, toastError, variant]);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between border-b bg-muted/20 px-6 py-4 text-left transition-colors hover:bg-muted/30"
        aria-expanded={open}
        data-testid="bulk-import-toggle"
      >
        <div className="flex items-center gap-2">
          <FileDown className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">Bulk create</span>
          <span className="text-sm text-muted-foreground">
            Copy AI prompt or paste JSON to create multiple rules
          </span>
        </div>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <CardContent className="flex flex-col gap-6 pt-6">
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-medium">AI generation prompt</CardTitle>
                <CardDescription className="mt-0.5">
                  Copy this prompt into Gemini or ChatGPT to generate rules
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleCopyPrompt}>
                <Copy className="mr-1.5 h-4 w-4" />
                Copy prompt
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="bulk-import-context" className="text-sm font-medium">
                Add your context (optional)
              </label>
              <textarea
                id="bulk-import-context"
                value={userContext}
                onChange={(e) => setUserContext(e.target.value)}
                placeholder="e.g. I want rules for my AU campaign. Focus on high-ROI."
                className="min-h-[72px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-label="Additional context for AI"
              />
            </div>
            <textarea
              readOnly
              value={fullPrompt}
              className="h-40 w-full resize-y rounded-md border border-input bg-muted/30 px-3 py-2 font-mono text-xs"
              aria-label="AI prompt"
            />
          </section>

          <div className="border-t border-border" />

          <section className="flex flex-col gap-3">
            <div>
              <CardTitle className="text-base font-medium">Import rules</CardTitle>
              <CardDescription className="mt-0.5">
                Paste JSON below. Use the prompt above to generate valid JSON from AI.
              </CardDescription>
            </div>
            <textarea
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setValidationError(null);
              }}
              placeholder='{"rules": [{ "name": "High Profit", "conditions": [{ "field": "profit", "operator": "gte", "value": 100 }] }]}'
              className="h-32 w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              aria-label="JSON input"
            />
            {validationError && (
              <p className="text-sm text-destructive">{validationError}</p>
            )}
            <Button
              onClick={handleImport}
              disabled={bulkCreate.isPending || !jsonInput.trim()}
            >
              {bulkCreate.isPending ? 'Importing…' : 'Import rules'}
            </Button>
          </section>
        </CardContent>
      )}
    </Card>
  );
};
